import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BellRing, CalendarClock, CalendarPlus, CheckCircle2, ClipboardCheck, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { api, errMsg, fmtDate, fmtINR, istDateISO } from "../lib/api";

/**
 * Phase 196 — follow-up visit actions shared by the collector To-Do (Today), the collector's Follow-Up page and the
 * supervisor's report:
 *   • FollowUpDoneDialog      — Done with a MANDATORY reason (dropdown): Owner Absent / House Locked / Payment Promised (+date,
 *                               auto-schedules the next follow-up) / Partial Payment Collected / Dispute or Complaint / Other (+text)
 *   • FollowUpRescheduleDialog — move the visit to a new date (logged; the property's follow-up count grows by one)
 *   • FollowUpDueTodayDialog  — "You have X follow-up visits due today" popup (once per IST day per user)
 */
export const DONE_REASONS = [
  { value: "owner_absent", label: "Owner Absent" },
  { value: "house_locked", label: "House Locked" },
  { value: "payment_promised", label: "Payment Promised (with date)" },
  { value: "partial_payment", label: "Partial Payment Collected" },
  { value: "dispute", label: "Dispute or Complaint" },
  { value: "other", label: "Other (describe)" },
];

export const FU_STATUS_META = {
  scheduled: { label: "Scheduled", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  due_today: { label: "Due today", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  overdue: { label: "Overdue", cls: "bg-red-50 text-red-800 border-red-200" },
  done: { label: "Done", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  superseded: { label: "Closed by newer visit", cls: "bg-muted text-muted-foreground border-border" },
  unscheduled: { label: "No date set", cls: "bg-amber-50 text-amber-900 border-amber-200" },
};

export function FollowUpStatusBadge({ status, label, testId, className = "" }) {
  const m = FU_STATUS_META[status] || FU_STATUS_META.scheduled;
  return <Badge variant="outline" className={`text-[11px] font-medium whitespace-nowrap ${m.cls} ${className}`} data-testid={testId} data-status={status}>{label || m.label}</Badge>;
}

export function RepeatedBadge({ count, testId, className = "" }) {
  return (
    <Badge variant="outline" className={`text-[11px] font-medium whitespace-nowrap gap-1 bg-orange-50 text-orange-900 border-orange-300 ${className}`}
      title={`${count} follow-ups on this property since its last payment — 3 or more without a payment need attention`} data-testid={testId}>
      <Repeat className="h-3 w-3" aria-hidden="true" /> Repeated Follow-up{count ? ` · ${count}` : ""}
    </Badge>
  );
}

const todayISO = () => istDateISO();

/* ------------------------------------------------------------------------------------------------ Done */
export function FollowUpDoneDialog({ code, item, open, onClose, onDone, testId = "fu-done" }) {
  const [reason, setReason] = useState("");
  const [promised, setPromised] = useState("");
  const [otherText, setOtherText] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setPromised(""); setOtherText(""); setRemarks(""); } }, [open, item?.id]);

  const needsDate = reason === "payment_promised";
  const needsText = reason === "other";
  const canSubmit = !!reason && (!needsDate || !!promised) && (!needsText || otherText.trim().length >= 5) && !busy;

  const submit = async () => {
    if (!canSubmit || !item) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/follow-up-visits/${item.id}/done`, {
        reason, remarks: remarks.trim() || null, promised_date: needsDate ? promised : null, other_text: needsText ? otherText.trim() : null,
      });
      const nf = r.data?.new_follow_up;
      toast.success(`Follow-up on ${item.pin} marked Done`, {
        description: nf ? `Payment promised for ${fmtDate(nf.follow_up_date)} — a new follow-up is scheduled for that day.` : `Reason: ${DONE_REASONS.find((x) => x.value === reason)?.label || reason}`,
      });
      onDone?.(r.data);
      onClose?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Mark follow-up as Done</DialogTitle>
          <DialogDescription>
            {item ? <><span className="font-mono text-foreground">{item.pin}</span> · {item.owner_name || "—"}{item.follow_up_date ? <> · scheduled {fmtDate(item.follow_up_date)}</> : null}{item.balance_due ? <> · {fmtINR(item.balance_due)} due</> : null}</> : null}
            <span className="block mt-1">Choose what happened on this visit — the reason is mandatory and is shown to your Supervisor and the Admin.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-11" data-testid={`${testId}-reason`}><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {DONE_REASONS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`${testId}-reason-${r.value}`}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {needsDate && (
            <div className="space-y-1 rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid={`${testId}-promised-block`}>
              <Label className="text-xs text-sky-900">Date the owner promised to pay *</Label>
              <Input type="date" className="h-11 bg-background" value={promised} min={todayISO()} onChange={(e) => setPromised(e.target.value)} data-testid={`${testId}-promised-date`} />
              <p className="text-[11px] text-sky-900/80 flex items-start gap-1.5"><CalendarPlus className="h-3.5 w-3.5 shrink-0 mt-0.5" /> A new follow-up visit is scheduled automatically on this date and appears in your To-Do.</p>
            </div>
          )}
          {needsText && (
            <div className="space-y-1">
              <Label className="text-xs">Describe the reason *</Label>
              <Textarea rows={3} value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="At least 5 characters" data-testid={`${testId}-other-text`} />
              {otherText.trim().length > 0 && otherText.trim().length < 5 && <p className="text-[11px] text-destructive">At least 5 characters.</p>}
            </div>
          )}
          {!needsText && (
            <div className="space-y-1">
              <Label className="text-xs">Remarks (optional)</Label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Anything the office should know" data-testid={`${testId}-remarks`} />
            </div>
          )}
          {item?.fu_status === "overdue" && (
            <p className="text-[11px] rounded-md border border-red-200 bg-red-50 text-red-900 px-3 py-2 flex gap-2" data-testid={`${testId}-overdue-note`}>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> This follow-up was due on {fmtDate(item.follow_up_date)} — it is recorded as completed late.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid={`${testId}-submit`}>{busy ? "Saving…" : "Mark Done"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------------------------------------ Reschedule */
export function FollowUpRescheduleDialog({ code, item, open, onClose, onDone, testId = "fu-reschedule" }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDate(""); setNote(""); } }, [open, item?.id]);
  const first = !item?.follow_up_date;
  const canSubmit = !!date && date !== item?.follow_up_date && !busy;
  const nextCount = (item?.follow_up_count || 0) + 1;

  const submit = async () => {
    if (!canSubmit || !item) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/follow-up-visits/${item.id}/reschedule`, { new_date: date, note: note.trim() || null });
      toast.success(`${first ? "Follow-up scheduled" : "Follow-up rescheduled"} for ${fmtDate(date)}`, {
        description: r.data?.repeated ? `This property has now been followed up ${r.data.follow_up_count} times without a payment — flagged as Repeated Follow-up.`
          : `Follow-up #${r.data?.follow_up_count ?? nextCount} on this property since its last payment.`,
      });
      onDone?.(r.data);
      onClose?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CalendarClock className="h-4 w-4" /> {first ? "Schedule the follow-up" : "Reschedule follow-up"}</DialogTitle>
          <DialogDescription>
            {item ? <><span className="font-mono text-foreground">{item.pin}</span> · {item.owner_name || "—"}{item.follow_up_date ? <> · currently {fmtDate(item.follow_up_date)}</> : null}</> : null}
            <span className="block mt-1">{first ? "Pick the date for the return visit." : "Pick the new date instead of marking it Done."} Every move is logged and counts as one more follow-up on this property{nextCount >= 3 ? " — it will show as Repeated Follow-up to the office" : ""}.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">New follow-up date *</Label>
            <Input type="date" className="h-11" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid={`${testId}-date`} />
            {date && date === item?.follow_up_date && <p className="text-[11px] text-destructive">That is already the scheduled date.</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Owner asked to come after the 15th" data-testid={`${testId}-note`} />
          </div>
          {(item?.reschedules || []).length > 0 && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground" data-testid={`${testId}-log`}>
              <p className="font-medium text-foreground mb-1">Rescheduled {item.reschedules.length} time{item.reschedules.length === 1 ? "" : "s"}</p>
              {item.reschedules.slice(-3).reverse().map((r, i) => (
                <p key={i}>{fmtDate(r.from)} → {fmtDate(r.to)} · {fmtDate((r.at || "").slice(0, 10))}{r.by_name ? ` · ${r.by_name}` : ""}{r.note ? ` · ${r.note}` : ""}</p>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid={`${testId}-submit`}>{busy ? "Saving…" : first ? "Schedule" : "Reschedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------------------------------------ Due-today popup */
const popupKey = (code, userId, day) => `fu-due-popup:${code}:${userId}:${day}`;

/** Loads today's follow-ups once and opens the popup the first time the collector opens the app on that IST day. */
export function useDueTodayPopup(code, userId, enabled = true) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = React.useCallback((autoOpen) => {
    if (!enabled || !code || !userId) return Promise.resolve();
    return api.get(`/t/${code}/follow-up-visits/today`).then((r) => {
      setData(r.data);
      const day = r.data?.today || todayISO();
      if (autoOpen && (r.data?.count || 0) > 0 && !localStorage.getItem(popupKey(code, userId, day))) {
        setOpen(true);
        localStorage.setItem(popupKey(code, userId, day), "1");
      }
    }).catch(() => {});
  }, [code, userId, enabled]);
  useEffect(() => { load(true); }, [load]);
  return { data, open, setOpen, reload: () => load(false) };
}

export function FollowUpDueTodayDialog({ data, open, onClose, propertyPath, onAct, testId = "fu-due-today" }) {
  const items = data?.items || [];
  const n = data?.count || items.length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><BellRing className="h-5 w-5 text-indigo-700" /> <span data-testid={`${testId}-title`}>You have {n} follow-up visit{n === 1 ? "" : "s"} due today</span></DialogTitle>
          <DialogDescription>{data?.today ? fmtDate(data.today) : ""} · Visit these properties today, then mark each one Done with a reason or reschedule it.{data?.overdue > 0 ? ` You also have ${data.overdue} overdue follow-up${data.overdue === 1 ? "" : "s"} in your To-Do.` : ""}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1" data-testid={`${testId}-list`}>
          {items.map((v) => (
            <div key={v.id} className="border rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`${testId}-item-${v.pin}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate"><span className="font-mono">{v.pin}</span> · {v.owner_name || "—"}</p>
                <p className="text-[11px] text-muted-foreground truncate">{[v.ward_label, v.mohalla_label].filter(Boolean).join(" › ")}{v.balance_due ? ` · ${fmtINR(v.balance_due)} due` : ""}{v.notes ? ` · ${v.notes}` : ""}</p>
                {v.repeated && <RepeatedBadge count={v.follow_up_count} className="mt-1" />}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {propertyPath && <Button asChild variant="outline" size="sm" className="h-8"><Link to={propertyPath(v)} onClick={onClose} data-testid={`${testId}-open-${v.pin}`}>Open</Link></Button>}
                {onAct && <Button size="sm" className="h-8" onClick={() => { onClose?.(); onAct(v); }} data-testid={`${testId}-done-${v.pin}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Done</Button>}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid={`${testId}-close`}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Row-level action buttons (Done + Reschedule) with their dialogs — drop into any list. */
export function FollowUpRowActions({ code, item, onChanged, size = "sm", compact = false, testIdPrefix = "fu" }) {
  const [doneOpen, setDoneOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const open = (item?.status || "open") === "open";
  const label = useMemo(() => (item?.follow_up_date ? "Reschedule" : "Set date"), [item?.follow_up_date]);
  if (!open) return null;
  return (
    <>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button variant="outline" size={size} className={compact ? "h-8 px-2" : "h-9"} onClick={() => setMoveOpen(true)} data-testid={`${testIdPrefix}-reschedule-${item.pin}`}>
          <CalendarClock className="h-3.5 w-3.5 mr-1" /> {label}
        </Button>
        <Button size={size} className={compact ? "h-8 px-2" : "h-9"} onClick={() => setDoneOpen(true)} data-testid={`${testIdPrefix}-done-${item.pin}`}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Done
        </Button>
      </div>
      <FollowUpDoneDialog code={code} item={item} open={doneOpen} onClose={() => setDoneOpen(false)} onDone={onChanged} testId={`${testIdPrefix}-done`} />
      <FollowUpRescheduleDialog code={code} item={item} open={moveOpen} onClose={() => setMoveOpen(false)} onDone={onChanged} testId={`${testIdPrefix}-reschedule`} />
    </>
  );
}
